
import { telemetry } from '../../../@vendor/Posthog';
import { uuid, waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import {
    JiraIssueCreateResult,
    JiraIssueData,
    JiraIssueLiteData,
    JiraIssueTypeData,
    JiraPriorityData,
    JiraTransitionData,
    JiraUserData
} from '../../../api/endpoints/jira';
import { CommandContext, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { RecordSetManager } from '../../../concepts/objects/RecordSetManager';
import { tryConvert } from '../../../platform/Convert';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { MyRoleInfo } from '../../canvas/MyRoleInfo';
import { resolvePhysical, resolveSubjects } from '../../canvas/commands/CommandUtil';
import { place } from '../../canvas/commands/OrderCommands';
import { select } from '../../canvas/commands/SelectionCommands';
import { Phys } from '../../canvas/entities/Phys';
import { RelativeTo } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { Textable } from '../../canvas/entities/Textable';
import { createJiraIssuesPopup } from '../../external/atlassian/CreateJiraIssuesPopup';
import { jiraOAuthRequiredPopup } from '../../external/atlassian/JiraOAuthRequiredPopup';
import { selectJiraProjectPopup } from '../../external/atlassian/SelectJiraProjectPopup';
import { busyPopup } from '../../popups/BusyPopup';
import { PopupManager } from '../../popups/PopupManager';
import { alert } from '../alerts/AlertCommands';
import { AlertHub } from '../alerts/AlertHub';
import { AutoLayout } from '../autoLayout/AutoLayout';
import { autoLayout } from '../autoLayout/AutoLayoutCommands';
import { FlexLayout } from '../autoLayout/FlexLayout';
import { Connector } from '../connector/Connector';
import { AtlassianContext } from './AtlassianContext';
import { BindingManager } from './BindingManager';
import { importFromJiraPopup } from './ImportFromJiraPopup';
import { importFromJiraProjectPopup } from './ImportFromJiraProjectPopup';
import { JiraCard } from './JiraCard';
import { configureVisibleJiraCustomFieldsPopup } from './JiraCardVisibleCustomFieldsPopup';

const jiraCardPatchInternal = (cc: CommandContext, patch: Record<string, unknown>, targets?: string[]) => {

    const subjects = Phys
        .from(resolveSubjects(cc, targets))
        .polarize(JiraCard)
        .toArray();

    if (!subjects.length) return;

    const atlassian = cc.resolve(AtlassianContext);
    const bindings = cc.resolve(BindingManager);

    if (atlassian.ready()) {

        for (const card of subjects) {
            bindings.lookup(card)?.mutate(patch);

            if (!!card.externalId) {
                atlassian.queueIssuePatch({
                    jiraIssueId: card.externalId,
                    actions: Object.entries(patch).map(([prop, value]) => ({
                        type: 'set',
                        params: { prop, value, }
                    }))
                });
            }
        }
    }
    else {

        const alerts = cc.resolve(AlertHub);
        alerts.invoke({
            type: 'error',
            // eslint-disable-next-line max-len
            body: "Can't perform updates without an Atlassian connection.  Please connect your Ludi and Atlassian accounts via Settings > Connections.",
            delay: 10000,
        });
    }

};

export const jiraCardAssign = defineCommand(
    'jiraAssign',
    'Jira Card: Assign...',
    (cc, [assignee, targets]: [JiraUserData, string[]?]) => {
        jiraCardPatchInternal(cc, { assignee }, targets);
    },
    [
        stringStep('jiraUserId', 'Jira User Id:')
    ]
);

export const jiraCardSetParent = defineCommand(
    'jiraCardSetParent',
    'Jira Card: Set Parent...',
    (cc, [parent, targets]: [JiraIssueLiteData, string[]?]) => {
        jiraCardPatchInternal(cc, { parent }, targets);
    },
    [
        stringStep('issueTypeId', 'Jira Issue Type Id:')
    ]
);

export const jiraCardSetIssueType = defineCommand(
    'jiraCardSetIssueType',
    'Jira Card: Set IssueType...',
    (cc, [issueType, targets]: [JiraIssueTypeData, string[]?]) => {
        jiraCardPatchInternal(cc, { issueType }, targets);
    },
    [
        stringStep('issueTypeId', 'Jira Issue Type Id:')
    ]
);

export const jiraCardSetPriority = defineCommand(
    'jiraCardSetPriority',
    'Jira Card: Set Priority...',
    (cc, [priority, targets]: [JiraPriorityData, string[]?]) => {
        jiraCardPatchInternal(cc, { priority }, targets);
    },
    [
        stringStep('priorityId', 'Jira Priority Id:')
    ]
);

export const jiraCardSetSummary = defineCommand(
    'jiraCardSetSummary',
    'Jira Card: Set Summary...',
    (cc, [summary, targets]: [string, string[]?]) => {
        jiraCardPatchInternal(cc, { summary }, targets);
    },
    [
        stringStep('summary', 'Summary:')
    ]
);

export const jiraCardSetCustom = defineCommand(
    'jiraCardSetCustom',
    'Jira Card: Set Custom...',
    (cc, [prop, value, targets]: [string, string, string[]?]) => {
        if (!prop || !value) return;

        const subjects = Phys
            .from(resolveSubjects(cc, targets))
            .polarize(JiraCard)
            .toArray();

        if (!subjects.length) return;

        const atlassian = cc.resolve(AtlassianContext);
        const bindings = cc.resolve(BindingManager);
        if (atlassian.ready()) {

            for (const card of subjects) {

                const info = card.customs.find(x => x.key === prop);
                if (!info) continue;
                value = tryConvert(value, info.type) ?? value;

                bindings.lookup(card)?.mutate({ custom: { [prop]: { ...info, value } } });

                if (!!card.externalId) {
                    atlassian.queueIssuePatch({
                        jiraIssueId: card.externalId,
                        actions: [{
                            type: 'set',
                            params: { prop, value, custom: true }
                        }]
                    });
                }
            }
        }
        else {

            const alerts = cc.resolve(AlertHub);
            alerts.invoke({
                type: 'error',
                // eslint-disable-next-line max-len
                body: "Can't perform updates without an Atlassian connection.  Please connect your Ludi and Atlassian accounts via the Settings > Connections.",
                delay: 10000,
            });
        }
    },
    [
        stringStep('name', 'Field Name:'),
        stringStep('value', 'Value:')
    ]
);

export const jiraCardTransition = defineCommand(
    'jiraTransition',
    'Jira Card: Transition...',
    (cc, [transition, targets]: [JiraTransitionData, string[]?]) => {

        const subjects = Phys
            .from(resolveSubjects(cc, targets))
            .polarize(JiraCard)
            .filter(x => !!x.externalId)
            .toArray();

        if (!subjects.length) return;

        const atlassian = cc.resolve(AtlassianContext);
        const bindings = cc.resolve(BindingManager);

        for (const card of subjects) {
            bindings.lookup(card)?.mutate({ status: transition.to });

            if (!!card.externalId) {
                atlassian.queueIssuePatch({
                    jiraIssueId: card.externalId,
                    actions: [{
                        type: 'transition',
                        params: { transitionId: transition.id }
                    }]
                });
            }
        }
    },
    [
        stringStep('transitionId', 'Transition Id:')
    ]
);

export const jiraCardOpenInJira = defineCommand(
    'jiraCardOpenInJira',
    'Jira Card: Open In Jira',
    (cc, [targetIds]: [string[]]) => {

        const subject = Phys.from(resolveSubjects(cc, targetIds)).polarize(JiraCard);
        for (const s of subject) {
            const url = s?.record?.url;
            if (url) {
                window.open(url, '_blank');
            }
        }
    }
);

export const jiraLinkProject = defineCommand(
    'jiraLinkProject',
    'Jira: Link Project',
    async (cc, [jiraProjectId]: [string]) => {

        const api = cc.resolve(ApiClient);
        const board = cc.resolve(EditorSession).get(BoardInfo);

        await api.exec(v2.jira.projects.links.create(jiraProjectId, board.id));
    },
    [
        stringStep('jiraProjectId', 'Jira Project Id:')
    ]
);

export const jiraLoad = defineCommand(
    'jiraLoad',
    'Jira: Load',
    async (cc) => {

        const api = cc.resolve(ApiClient);
        const objects = cc.resolve(ObjectManager);
        const allCards = Array.from(objects.getAll(JiraCard));
        const board = cc.resolve(EditorSession).get(BoardInfo);

        const loadableCards = allCards.filter(x => !!x.externalId);
        if (!!loadableCards.length) {
            await api.exec(v2.jira.issues.load({
                boardId: board.id,
                jiraIssueIds: loadableCards.map(x => x.externalId),
            }));
        }
    }
);

export const jiraLoadMissing = defineCommand(
    'jiraLoadMissing',
    'Jira: Load Missing',
    async (cc) => {

        const api = cc.resolve(ApiClient);
        const objects = cc.resolve(ObjectManager);
        const allCards = Array.from(objects.getAll(JiraCard));
        const board = cc.resolve(EditorSession).get(BoardInfo);

        const recordSet = cc.resolve(RecordSetManager).resolve<JiraIssueData>('JiraIssues');
        const missing = allCards.filter(x => !!x.externalId && (recordSet.get(x.externalId)?.future ?? true));

        if (!!missing.length) {
            await api.exec(v2.jira.issues.load({
                boardId: board.id,
                jiraIssueIds: missing.map(x => x.externalId),
            }));
        }
    }
);

export const jiraReload = defineCommand(
    'jiraReload',
    'Jira: Reload',
    async (cc, [targetIds]: [string[]?]) => {

        const api = cc.resolve(ApiClient);
        const board = cc.resolve(EditorSession).get(BoardInfo);
        const reloadableCards = Phys.from(resolveSubjects(cc, targetIds))
            .polarize(JiraCard)
            .filter(x => !!x.externalId);

        if (!!reloadableCards.size) {
            await api.exec(v2.jira.issues.load({
                boardId: board.id,
                jiraIssueIds: reloadableCards.map(x => x.externalId),
            }));
        }
    }
);

export const jiraImport = defineCommand(
    'jiraImport',
    'Import: From Jira...',
    async (cc, [container, jql]: [any, string?]) => {

        telemetry.emit('jira.beginImport');

        const atlassian = cc.resolve(AtlassianContext);
        const objects = cc.resolve(ObjectManager);
        const popups = cc.resolve(PopupManager);
        const jiraIssues = cc.resolve(RecordSetManager).resolve('JiraIssues');

        if (!atlassian.ready()) {
            await popups.invoke(jiraOAuthRequiredPopup());
            if (!atlassian.ready()) return;
        }

        let issues: JiraIssueData[] = [];

        if (!!jql) {
            const { data } = await popups.invoke(importFromJiraPopup(jql));
            issues = data;
        }
        else {
            const { data: jiraProject } = await popups.invoke(selectJiraProjectPopup());
            if (!jiraProject) return;

            const { data } = await popups.invoke(importFromJiraProjectPopup(jiraProject));
            issues = data;
        }

        if (!issues?.length) return;

        await popups.invoke(busyPopup(
            async () => {
                const futures = issues.map((x: any) => {
                    atlassian.queueIssueLoad(x.id);
                    return jiraIssues.waitForFuture(x.id);
                });
                await Promise.all(futures);

                const key = uuid();

                const cards: JiraCard[] = objects.update(u => {
                    u.set('key', `jiraImport:${key}`);
                    return issues.map(tm =>
                        Physicals.createAtCameraPosition(cc, JiraCard, nc => {
                            nc.externalId = tm.id;
                            nc.key = tm.key;
                            nc.project = tm.projectId;
                        })
                    );
                });

                // Wait for binding manager to have done it's magic
                await waitFor(10);

                objects.update(u => {
                    u.set('key', `jiraImport:${key}`);

                    for (const c of cards) {
                        c.sizeToContent();
                    }

                    const cont = resolvePhysical(cc, container);
                    if (!!cont) {
                        FlexLayout.exec(cont.bounds(RelativeTo.Model), ['row', 'start', 'start', 10], cards);
                        cc.dispatch(place, [cards.map(x => x.id)]);
                        if (cont.is(AutoLayout)) {
                            cc.dispatch(autoLayout, [cont.id]);
                        }
                    }
                    else {
                        // Add 10 for spacing
                        const mw = Math.max(...cards.map(x => x.width)) + 10;
                        const mh = Math.max(...cards.map(x => x.height)) + 10;

                        const w = mw * Math.ceil(Math.sqrt(cards.length));
                        const h = mh * Math.ceil(Math.sqrt(cards.length));
                        const area = Rect.fromCenter(cards[0].center(RelativeTo.Model), new Point(w, h).divide(2));
                        FlexLayout.exec(area, ['row', 'start', 'start', 10], cards);
                    }
                });

                cc.dispatch(select, [cards.map(x => x.id)]);

            }
        ));

        telemetry.emit('jira.completeImport', { count: issues?.length || 0 });

    },
    [
        stringStep('containerId', 'Container:')
    ]
);

// export const jiraCreateIssue = defineCommand(
//     'jiraCreateIssue',
//     'Jira: Create Issue',
//     async (cc, [issueType, summary, description]: [string?, string?, string?]) => {

//         const atlassian = cc.resolve(AtlassianContext);
//         const popups = cc.resolve(PopupManager);
//         const objects = cc.resolve(ObjectManager);
//         const jiraIssuesRecordSet = cc.resolve(RecordSetManager).resolve('JiraIssues');

//         if (!atlassian.ready()) {
//             await popups.invoke(jiraOAuthRequiredPopup());
//             if (!atlassian.ready()) return;
//         }

//         const { data: jiraProject } = await popups.invoke(selectJiraProjectPopup());
//         if (!jiraProject) return;

//         const { data: info } = await popups.invoke(createJiraIssuesPopup({
//             jiraProject,
//             prefill: { issueType, summary, description },
//         }));
//         if (!info) return;

//         const updateKey = `jiraCreateIssue:${uuid()}`;

//         const card = objects.update(u => {
//             u.set('key', updateKey);
//             return Physicals.createAtCameraPosition(cc, JiraCard, nc => {
//                 nc.project = jiraProject.id;
//                 nc.externalId = info.id;
//                 nc.key = info.key;
//             });
//         });

//         await atlassian.queueIssueLoad(info.id);
//         await jiraIssuesRecordSet.waitForFuture(info.id);

//         // Wait for binding manager to have done it's magic
//         await waitFor(10);

//         // Once loaded, size the cards to their content
//         objects.update(u => {
//             u.set('key', updateKey);
//             card.sizeToContent();
//         });
//     }
// );

export const jiraCreateIssuesFrom = defineCommand(
    'jiraCreateIssuesFrom',
    'Jira: Create Issues From...',
    async (cc, [targetIds]: [string[]?]) => {

        const alerts = cc.resolve(AlertHub);
        const atlassian = cc.resolve(AtlassianContext);
        const jiraIssues = cc.resolve(RecordSetManager).resolve('JiraIssues');
        const objects = cc.resolve(ObjectManager);
        const popups = cc.resolve(PopupManager);

        if (!atlassian.ready()) {
            await popups.invoke(jiraOAuthRequiredPopup());
            if (!atlassian.ready()) return;
        }

        const subjects = resolveSubjects(cc, targetIds, Textable);
        if (!subjects.length) return;

        if (subjects.length > 20) {
            cc.dispatch(alert, ['Sorry, only up to 20 issues can be created at once.', 10000, 'error']);
            return;
        }

        telemetry.emit('jira.beginCreateIssuesFrom', { count: subjects.length });

        const { data: jiraProject } = await popups.invoke(selectJiraProjectPopup());
        if (!jiraProject) return;

        const updateKey = `jiraCreateIssuesFrom:${uuid()}`;
        const sources = subjects.map((s: Textable) => {
            const [summary, description] = s.toTextArray();
            return { localId: s.id, summary, description };
        });

        const createResult = await popups.invoke(createJiraIssuesPopup({
            jiraProject,
            sources,
        }));

        if (!createResult.actioned) return;
        const reply = createResult.data as JiraIssueCreateResult[];

        await popups.invoke(busyPopup(
            async () => {

                const messages = [] as string[];
                const jiraIssueIds = reply.map(x => x.id).filter(x => !!x);
                if (!!jiraIssueIds.length) {
                    // Issue load command for issues that were created & and wait for them to load
                    await Promise.all(
                        jiraIssueIds.map(async x => {
                            atlassian.queueIssueLoad(x);
                            try {
                                await jiraIssues.waitForFuture(x, 30 * 1000);
                            }
                            catch (e) {
                                messages.push(`Failed to load issue ${x}: ${e.message}`);
                            }
                        })
                    );
                }

                const cards = [] as JiraCard[];

                objects.update(u => {
                    u.set('key', updateKey);
                    u.set('history', 'skip');

                    for (const { ok, localId, id, key, errors } of reply) {
                        if (ok) {
                            const elmt = objects.getMaybe(localId, Textable);
                            if (!elmt) continue;

                            const card = u.create(JiraCard);
                            card.moveTo(elmt.center(RelativeTo.Parent).subtract(card.size().divide(2)));
                            card.project = jiraProject.id;
                            card.externalId = id;
                            card.key = key;

                            const connectors = elmt.relations('attached', Connector);
                            for (const conn of connectors) {
                                if (conn.fromId === elmt.id) conn.fromId = card.id;
                                if (conn.toId === elmt.id) conn.toId = card.id;
                            }

                            elmt.addToParentAfterSelf(card);
                            u.delete(elmt.removeSelf(), 'cascade');

                            cards.push(card);
                        }
                        else if (!!errors?.length) {
                            messages.push(errors.join('\n'));
                        }
                    }
                });

                // Wait for binding manager to have done it's magic
                await waitFor(10);

                // Once loaded, size the cards to their content
                objects.update(u => {
                    u.set('key', updateKey);
                    for (const c of cards) {
                        c.sizeToContent();
                    }
                });

                cc.dispatch(select, [cards.map(x => x.id)]);

                if (!!messages.length) {
                    alerts.invoke({
                        type: 'error',
                        body: messages.join('\n'),
                        delay: 10000,
                    });
                }

                telemetry.emit('jira.beginSave', { count: subjects.length, errors: messages.length });

            }
        ));
    }
);

export const jiraSaveAsIssues = defineCommand(
    'jiraSaveAsIssues',
    'Jira: Save As Issues',
    async (cc, [targetIds]: [string[]?]) => {

        telemetry.emit('jira.beginSave');

        const alerts = cc.resolve(AlertHub);
        const api = cc.resolve(ApiClient);
        const atlassian = cc.resolve(AtlassianContext);
        const board = cc.resolve(EditorSession).get(BoardInfo);
        const objects = cc.resolve(ObjectManager);
        const popups = cc.resolve(PopupManager);
        const jiraIssues = cc.resolve(RecordSetManager).resolve('JiraIssues');

        const elmts = Phys
            .from(resolveSubjects(cc, targetIds))
            .polarize(Textable)
            .toArray();

        if (!elmts.length) return;

        if (!atlassian.ready()) {
            await popups.invoke(jiraOAuthRequiredPopup());
            if (!atlassian.ready()) return;
        }

        const { actioned, data } = await popups.invoke(selectJiraProjectPopup());
        if (!actioned || !data) return;

        const jiraProjectId = data.id;
        const jiraIssueType = data.issueTypes.find(x => !x.subtask);

        await popups.invoke(busyPopup(
            async () => {

                const messages = [] as string[];
                const reply = await api.exec(v2.jira.issues.create(
                    elmts.map(x => ({
                        localId: x.id,
                        jiraProjectId,
                        issueType: { id: jiraIssueType.id },
                        summary: x.toText().replace(/\r?\n/g, ' '),
                    }))
                ));

                const jiraIssueIds = reply.filter(x => x.ok).map(x => x.id);
                if (!!jiraIssueIds.length) {
                    // Issue load command for issues that were created & and wait for them to load
                    await Promise.all(
                        jiraIssueIds.map(x => {
                            atlassian.queueIssueLoad(x);
                            return jiraIssues.waitForFuture(x);
                        })
                    );
                }

                const updateKey = `jiraSaveAsIssues:${uuid()}`;
                const cards = [] as JiraCard[];

                objects.update(u => {
                    u.set('key', updateKey);
                    u.set('history', 'skip');

                    for (const { ok, localId, id, key, errors } of reply) {
                        if (ok) {
                            const elmt = objects.getMaybe(localId, Textable);
                            if (!elmt) continue;

                            const card = u.create(JiraCard);
                            card.moveTo(elmt.center(RelativeTo.Parent).subtract(card.size().divide(2)));
                            card.project = jiraProjectId;
                            card.externalId = id;
                            card.key = key;

                            const connectors = elmt.relations('attached', Connector);
                            for (const conn of connectors) {
                                if (conn.fromId === elmt.id) conn.fromId = card.id;
                                if (conn.toId === elmt.id) conn.toId = card.id;
                            }

                            elmt.addToParentAfterSelf(card);
                            u.delete(elmt.removeSelf(), 'cascade');

                            cards.push(card);
                        }
                        else if (!!errors?.length) {
                            messages.push(errors.join('\n'));
                        }
                    }
                });

                // Wait for binding manager to have done it's magic
                await waitFor(10);

                // Once loaded, size the cards to their content
                objects.update(u => {
                    u.set('key', updateKey);
                    for (const c of cards) {
                        c.sizeToContent();
                    }
                });

                cc.dispatch(select, [cards.map(x => x.id)]);

                if (!!messages.length) {
                    alerts.invoke({
                        type: 'error',
                        body: messages.join('\n'),
                        delay: 10000,
                    });
                }

                telemetry.emit('jira.endSave', { count: elmts.length, errors: messages.length });

            }
        ));
    }
);

export const jiraCardFixSizes = defineCommand(
    'jiraCardFixSizes',
    'Jira Card: Fix Sizes',
    (cc) => {

        const role = cc.resolve(EditorSession).get(MyRoleInfo);
        if (!role.canParticipate) return;

        const objects = cc.resolve(ObjectManager);
        const cards = objects.getAll(JiraCard);

        objects.update(u => {
            u.set('history', 'skip');
            for (const c of cards) {
                c.sizeToContent();
            }
        });
    }
);

export const jiraConfigureVisibleCustomFields = defineCommand(
    'jiraConfigureVisibleCustomFields',
    'Jira: Configure Visible Custom Fields',
    async (cc) => {

        const popups = cc.resolve(PopupManager);
        await popups.invoke(configureVisibleJiraCustomFieldsPopup());
    }
);

export const inAtlassianCommands = () => inCommands(
    jiraCardAssign,
    jiraCardSetIssueType,
    jiraCardSetPriority,
    jiraCardSetSummary,
    jiraCardSetCustom,
    jiraCardTransition,
    jiraCardOpenInJira,
    jiraImport,
    jiraLinkProject,
    jiraLoad,
    jiraSaveAsIssues,
    jiraConfigureVisibleCustomFields,
);

